/* ==========================================================================
   Builder gel landing page motion.

   The sibling of nail-drill.js with the gb namespace. Three systems, all
   progressive enhancements. The CSS only hides or resets anything when
   this script has set data-gb-motion="on", so with JS off every stage
   renders its finished frame.

     reveal   panels fade and rise once, the first time they enter the
              viewport, with a self-removing scroll sweep as the safety
              net for scrolls that outrun IntersectionObserver delivery.

     count    stat values run up from zero, once. The final frame
              restores the exact string the merchant typed.

     stages   the level and the cure. From 750px up they pin and play as
              pure functions of scroll position, reversible and exact.
              Below 750px they never pin: mobile browser chrome resizes
              the layout viewport in one discrete step as it hides, and
              a full screen sticky stage re-anchors in a single frame,
              which reads as the page jumping. On phones the stages sit
              in normal flow and their animation plays on time, once,
              when the stage enters the viewport. The 750px line must
              match the media query in gel-builder.css.

   Parallax on the full bleed moments is transform only and runs at
   every width. Everything no-ops under prefers-reduced-motion, and
   re-inits on shopify:section:load so the theme editor stays in sync.
   ========================================================================== */

(function () {
  'use strict';

  var SELECTOR = '.gel-builder';
  var PINNED_MQ = '(min-width: 750px)';
  var REVEAL_STEP = 90;
  var COUNT_MS = 1500;
  var DIAL_PLAY_MS = 1700;
  var WAVE_PLAY_MS = 1500;
  var PARALLAX_TRAVEL = 38;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function easeOutExpo(t) {
    return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
  }

  function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function formatLike(value) {
    return Math.round(value).toLocaleString(document.documentElement.lang || undefined);
  }

  /* ------------------------------------------------------------------------
     Stat count up, one shot
     ------------------------------------------------------------------------ */

  function runCount(el) {
    if (el.dataset.gbCounted === 'true') return;
    el.dataset.gbCounted = 'true';

    var finalText = el.textContent;
    var target = parseFloat(finalText.replace(/[^\d.]/g, ''));

    if (!isFinite(target) || target <= 0) return;

    var decimals = (finalText.split('.')[1] || '').replace(/\D/g, '').length;
    var locale = document.documentElement.lang || undefined;
    var start = null;

    function frame(now) {
      if (start === null) start = now;

      var progress = Math.min((now - start) / COUNT_MS, 1);

      if (progress < 1) {
        el.textContent = (target * easeOutExpo(progress)).toLocaleString(locale, {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals
        });
        window.requestAnimationFrame(frame);
      } else {
        el.textContent = finalText;
      }
    }

    window.requestAnimationFrame(frame);
  }

  /* ------------------------------------------------------------------------
     Reveal
     ------------------------------------------------------------------------ */

  function reveal(el, observer) {
    if (el.classList.contains('is-in')) return;

    var siblings = el.parentElement
      ? Array.prototype.filter.call(el.parentElement.children, function (child) {
          return child.classList.contains('gb-reveal');
        })
      : [el];

    el.style.setProperty('--gb-delay', Math.max(siblings.indexOf(el), 0) * REVEAL_STEP + 'ms');
    el.classList.add('is-in');

    if (observer) observer.unobserve(el);
  }

  function observeReveals(root) {
    var items = root.querySelectorAll('.gb-reveal');
    if (!items.length) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) reveal(entry.target, observer);
        });
      },
      // A fixed offset rather than a percentage: on a very tall viewport a
      // percentage can shrink the root past the last panel, which would
      // leave it hidden for good.
      { rootMargin: '0px 0px -80px 0px', threshold: 0.01 }
    );

    items.forEach(function (item) {
      observer.observe(item);
    });

    /* Safety net. A fast scroll can outrun IntersectionObserver delivery:
       an element that enters and leaves the viewport between two callbacks
       is never reported, and would stay at opacity zero for good. This
       sweep reveals anything at or above the fold, including content
       already scrolled past, and unhooks itself once every item is done. */
    var pending = Array.prototype.slice.call(items);
    var queued = false;

    function sweep() {
      queued = false;

      pending = pending.filter(function (item) {
        if (item.classList.contains('is-in')) return false;

        if (item.getBoundingClientRect().top < window.innerHeight) {
          reveal(item, observer);
          return false;
        }
        return true;
      });

      if (!pending.length) {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
      }
    }

    function onScroll() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(sweep);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    window.addEventListener('load', onScroll);
    onScroll();
  }

  function observeCounts(root) {
    var values = root.querySelectorAll('[data-gb-count]');
    if (!values.length) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          runCount(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.5 }
    );

    values.forEach(function (value) {
      observer.observe(value);
    });
  }

  /* ------------------------------------------------------------------------
     Stage rendering, shared by both modes
     ------------------------------------------------------------------------ */

  function renderDial(dial, eased, resolveQ) {
    if (dial.number) {
      dial.number.textContent =
        eased >= 1 ? dial.finalText : formatLike(dial.target * eased);
    }

    if (dial.ring) {
      dial.ring.style.strokeDashoffset = String(dial.circumference * (1 - eased));
    }

    if (dial.resolve) {
      dial.resolve.style.opacity = String(resolveQ);
      dial.resolve.style.transform = 'translate3d(0, ' + 18 * (1 - resolveQ) + 'px, 0)';
    }
  }

  function renderWave(wave, p) {
    if (wave.form) {
      // The ridges settle onto the baseline, then dissolve into it: the
      // hairline that remains is the leveled surface.
      wave.form.style.transform = 'scaleY(' + (1 - 0.96 * easeOutCubic(p)) + ')';
      wave.form.style.opacity = String(1 - clamp((p - 0.74) / 0.2, 0, 1));
    }

    if (wave.intro) {
      wave.intro.style.opacity = String(1 - clamp((p - 0.35) / 0.25, 0, 1));
    }

    if (wave.resolve) {
      var q = clamp((p - 0.7) / 0.22, 0, 1);
      wave.resolve.style.opacity = String(q);
      wave.resolve.style.transform = 'translate3d(0, ' + 18 * (1 - q) + 'px, 0)';
    }
  }

  /* Progress for the pinned mode is measured against the stage's own
     rendered height, never against window.innerHeight: on phones the
     browser chrome slides away mid scroll and innerHeight steps by the
     toolbar's height. The stage height also puts p = 1 exactly where the
     stage unpins, so the finished frame hands off with no dead zone. */
  function stageProgress(el, stageHeight) {
    var rect = el.getBoundingClientRect();
    var span = rect.height - stageHeight;
    if (span <= 0) return 1;
    return clamp(-rect.top / span, 0, 1);
  }

  /* ------------------------------------------------------------------------
     Timed playback for phones
     ------------------------------------------------------------------------ */

  function playDial(dial) {
    if (dial.played) return;
    dial.played = true;

    var start = null;

    function tick(now) {
      if (start === null) start = now;

      var p = Math.min((now - start) / DIAL_PLAY_MS, 1);
      var eased = easeOutExpo(p);

      renderDial(dial, eased, clamp((p - 0.72) / 0.24, 0, 1));

      if (p < 1) window.requestAnimationFrame(tick);
    }

    window.requestAnimationFrame(tick);
  }

  function playWave(wave) {
    if (wave.played) return;
    wave.played = true;

    var start = null;

    function tick(now) {
      if (start === null) start = now;

      var p = Math.min((now - start) / WAVE_PLAY_MS, 1);

      renderWave(wave, p);

      if (p < 1) window.requestAnimationFrame(tick);
    }

    window.requestAnimationFrame(tick);
  }

  function finishDial(dial) {
    if (dial.played) return;
    dial.played = true;
    renderDial(dial, 1, 1);
  }

  function finishWave(wave) {
    if (wave.played) return;
    wave.played = true;
    renderWave(wave, 1);
  }

  /* ------------------------------------------------------------------------
     Scroll engine
     ------------------------------------------------------------------------ */

  function initScrollEngine(root) {
    var pinned = window.matchMedia(PINNED_MQ).matches;

    var dials = Array.prototype.map.call(root.querySelectorAll('[data-gb-dial]'), function (el) {
      var number = el.querySelector('[data-gb-dial-number]');
      var ring = el.querySelector('[data-gb-dial-ring]');
      var stage = el.querySelector('.gb-dial__stage');
      var circumference = 0;

      if (ring) {
        circumference = 2 * Math.PI * (parseFloat(ring.getAttribute('r')) || 48);
        ring.style.strokeDasharray = String(circumference);
      }

      return {
        el: el,
        stage: stage,
        stageH: stage ? stage.offsetHeight : window.innerHeight,
        number: number,
        finalText: number ? number.textContent : '',
        target: parseFloat(el.getAttribute('data-gb-dial')) || 0,
        ring: ring,
        circumference: circumference,
        resolve: el.querySelector('[data-gb-dial-resolve]'),
        played: false
      };
    });

    var waves = Array.prototype.map.call(root.querySelectorAll('[data-gb-wave]'), function (el) {
      var stage = el.querySelector('.gb-wave__stage');
      return {
        el: el,
        stage: stage,
        stageH: stage ? stage.offsetHeight : window.innerHeight,
        form: el.querySelector('[data-gb-wave-form]'),
        intro: el.querySelector('[data-gb-wave-intro]'),
        resolve: el.querySelector('[data-gb-wave-resolve]'),
        played: false
      };
    });

    var parallaxes = Array.prototype.map.call(
      root.querySelectorAll('[data-gb-parallax]'),
      function (el) {
        return { el: el, host: el.closest('.gb-moment') || el.parentElement };
      }
    );

    if (!dials.length && !waves.length && !parallaxes.length) return;

    var ticking = false;

    /* The parallax normalizer is cached and only refreshed when the width
       changes: a height only resize is the phone's toolbar sliding away
       mid scroll, and following it would nudge every frame. */
    var viewW = window.innerWidth;
    var viewH = window.innerHeight;

    function refreshStageHeights() {
      dials.forEach(function (dial) {
        if (dial.stage) dial.stageH = dial.stage.offsetHeight;
      });
      waves.forEach(function (wave) {
        if (wave.stage) wave.stageH = wave.stage.offsetHeight;
      });
    }

    function renderParallaxes() {
      parallaxes.forEach(function (item) {
        var rect = item.host.getBoundingClientRect();
        var mid = rect.top + rect.height / 2 - viewH / 2;
        var ratio = clamp(mid / ((viewH + rect.height) / 2), -1, 1);
        item.el.style.setProperty('--gb-py', (ratio * -PARALLAX_TRAVEL).toFixed(1) + 'px');
      });
    }

    function frame() {
      ticking = false;

      if (pinned) {
        dials.forEach(function (dial) {
          var eased = easeOutCubic(stageProgress(dial.el, dial.stageH));
          renderDial(dial, eased, clamp((eased - 0.82) / 0.16, 0, 1));
        });

        waves.forEach(function (wave) {
          renderWave(wave, stageProgress(wave.el, wave.stageH));
        });
      } else {
        /* Timed playback owns the stages on phones; the scroll pass only
           finishes anything a fast jump carried past the viewport before
           its observer could fire. */
        dials.forEach(function (dial) {
          if (!dial.played && dial.el.getBoundingClientRect().bottom < 0) finishDial(dial);
        });
        waves.forEach(function (wave) {
          if (!wave.played && wave.el.getBoundingClientRect().bottom < 0) finishWave(wave);
        });
      }

      renderParallaxes();
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(frame);
    }

    function onResize() {
      if (window.innerWidth !== viewW) {
        viewW = window.innerWidth;
        viewH = window.innerHeight;
        refreshStageHeights();
      }
      onScroll();
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });

    if (!pinned) {
      // The stages start silent: zero on the dial, the surface ridged, both
      // resolved lines hidden by the motion-on CSS until playback.
      dials.forEach(function (dial) {
        if (dial.number) dial.number.textContent = formatLike(0);
      });

      var stages = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;

            dials.forEach(function (dial) {
              if (dial.stage === entry.target || dial.el === entry.target) playDial(dial);
            });
            waves.forEach(function (wave) {
              if (wave.stage === entry.target || wave.el === entry.target) playWave(wave);
            });

            stages.unobserve(entry.target);
          });
        },
        { threshold: 0.55 }
      );

      dials.forEach(function (dial) {
        stages.observe(dial.stage || dial.el);
      });
      waves.forEach(function (wave) {
        stages.observe(wave.stage || wave.el);
      });
    }

    frame();
  }

  /* ------------------------------------------------------------------------
     Init
     ------------------------------------------------------------------------ */

  function init(scope) {
    var roots = (scope || document).querySelectorAll(SELECTOR);

    roots.forEach(function (root) {
      if (root.dataset.gbReady === 'true') return;
      root.dataset.gbReady = 'true';

      if (prefersReducedMotion() || !('IntersectionObserver' in window)) return;

      root.setAttribute('data-gb-motion', 'on');

      observeReveals(root);
      observeCounts(root);
      initScrollEngine(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
